import React, { useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";

import aboutusImg from "./images/aboutusimg.jpeg";
import partnerships from "./images/partnerships.png";
import delivery from "./images/delivery.png";
import support from "./images/support.png";
import solutions from "./images/solutions.png";

function AboutPage() {
  useEffect(() => {
    document.title = "About Us | Prosep Services";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Learn about Prosep Services Ltd — a multi-sector supplier of premium mining, laboratory, and security products across Ghana and West Africa.";

    return () => {
      document.title = "Prosep Services";
    };
  }, []);

  const highlights = [
    {
      num: "01",
      title: "Strong supplier partnerships",
      desc: "Long-standing relationships with world-class manufacturers like ABUS, Cabot, Sepro, and Minelab.",
      bg: partnerships,
    },
    {
      num: "02",
      title: "Quick sourcing and delivery",
      desc: "Efficient procurement channels and logistics that get your order to you without delay.",
      bg: delivery,
    },
    {
      num: "03",
      title: "Expert product support",
      desc: "Technical guidance from specialists who understand mining, lab, and security equipment.",
      bg: support,
    },
    {
      num: "04",
      title: "Tailored solutions for every sector",
      desc: "Custom configurations designed for mining, education, research, and construction.",
      bg: solutions,
    },
  ];

  return (
    <>
      <Header />

      <main className="about-page">
        {/* ============ HERO ============ */}
        <section className="about-hero">
          <div className="about-hero-inner">
            <h1 className="about-hero-title">
              About <span className="about-hero-accent">Us.</span>
            </h1>
            <p className="about-hero-subtitle">
              Your trusted source for mining, laboratory & security solutions
              across Ghana and West Africa.
            </p>
          </div>
        </section>

        {/* ============ WHO WE ARE + IMAGE (side by side) ============ */}
        <section className="about-section">
          <div className="about-section-inner">
            <div className="about-who-grid">
              {/* LEFT: text */}
              <div className="about-who-text">
                <h2 className="about-block-title about-block-title-left">
                  Who We Are
                </h2>
                <p className="about-block-text about-block-text-left">
                  Prosep Services Ltd is a multi-sector supplier of premium
                  mining, laboratory, and security products. We partner with
                  top global brands like ABUS to serve clients in mining,
                  education, research, and construction industries.
                </p>
              </div>

              {/* RIGHT: image */}
              <div className="about-who-image">
                <div className="about-image-frame">
                  <img src={aboutusImg} alt="Prosep Services products" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ VISION & MISSION ============ */}
        <section className="about-section about-section-alt">
          <div className="about-section-inner">
            <div className="about-two-col">
              <div className="about-card">
                <div className="about-card-icon">🎯</div>
                <h3 className="about-card-title">Our Vision</h3>
                <p className="about-card-text">
                  To be indispensable partners facilitating progress through
                  unmatched access, expertise and innovation to all
                  stakeholders.
                </p>
              </div>

              <div className="about-card">
                <div className="about-card-icon">🚀</div>
                <h3 className="about-card-title">Our Mission</h3>
                <p className="about-card-text">
                  Our mission is to ensure seamless flow of high quality
                  solutions to empower industries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ WHAT SETS US APART ============ */}
        <section className="about-section">
          <div className="about-section-inner">
            <div className="about-highlights-head">
              <h2 className="about-block-title">What Sets Us Apart</h2>
            </div>

            <div className="about-highlights-grid">
              {highlights.map((item) => (
                <div
                  className="about-highlight"
                  key={item.num}
                  style={{ backgroundImage: `url(${item.bg})` }}
                >
                  <div className="about-highlight-overlay">
                    <span className="about-highlight-num">{item.num}</span>
                    <div className="about-highlight-content">
                      <h3 className="about-highlight-title">{item.title}</h3>
                      <p className="about-highlight-desc">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .about-page {
          width: 100%;
          background: #ffffff;
          font-family: Arial, sans-serif;
        }

        /* ============ HERO ============ */
        .about-hero {
          width: 100%;
          padding: 110px 40px 90px;
          background: linear-gradient(
            135deg,
            #001a3d 0%,
            #002a5c 50%,
            #0047a3 100%
          );
          text-align: center;
          box-sizing: border-box;
        }
        .about-hero-inner {
          max-width: 900px;
          margin: 0 auto;
        }
        .about-hero-title {
          font-size: 56px;
          font-weight: 900;
          color: #ffffff;
          margin: 0 0 20px;
          letter-spacing: 0.5px;
          line-height: 1.1;
        }
        .about-hero-accent {
          color: #b8965a;
        }
        .about-hero-subtitle {
          font-size: 19px;
          line-height: 1.6;
          color: #dbe5f0;
          margin: 0;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        /* ============ COMMON SECTION ============ */
        .about-section {
          width: 100%;
          padding: 90px 40px;
          box-sizing: border-box;
        }
        .about-section-alt {
          background: #f7f9fc;
        }
        .about-section-inner {
          max-width: 1400px;
          margin: 0 auto;
        }

        /* ============ WHO WE ARE (side by side) ============ */
        .about-who-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .about-who-text {
          display: flex;
          flex-direction: column;
        }

        .about-block-title {
          font-size: 34px;
          font-weight: 900;
          color: #111;
          margin: 0 0 22px;
          letter-spacing: 0.3px;
          position: relative;
          display: inline-block;
        }
        .about-block-title::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -12px;
          transform: translateX(-50%);
          width: 48px;
          height: 3px;
          background: #c8102e;
        }
        .about-block-title-left::after {
          left: 0;
          transform: none;
        }
        .about-block-text {
          font-size: 17px;
          line-height: 1.8;
          color: #4b5563;
          margin: 32px 0 0;
        }
        .about-block-text-left {
          margin: 32px 0 0;
          max-width: 100%;
        }

        /* Image frame */
        .about-who-image {
          width: 100%;
        }
        .about-image-frame {
          position: relative;
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
          background: #f7f9fc;
        }
        .about-image-frame img {
          display: block;
          width: 100%;
          height: auto;
          max-height: 520px;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .about-image-frame:hover img {
          transform: scale(1.03);
        }

        /* ============ VISION & MISSION ============ */
        .about-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
        }
        .about-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 40px 32px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
          transition: all 0.25s ease;
          text-align: center;
        }
        .about-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
          border-color: #c8102e;
        }
        .about-card-icon {
          font-size: 42px;
          margin-bottom: 18px;
          display: inline-block;
        }
        .about-card-title {
          font-size: 22px;
          font-weight: 800;
          color: #111;
          margin: 0 0 16px;
        }
        .about-card-text {
          font-size: 16px;
          line-height: 1.75;
          color: #4b5563;
          margin: 0;
        }

        /* ============ WHAT SETS US APART ============ */
        .about-highlights-head {
          text-align: center;
          margin-bottom: 60px;
        }
        .about-highlights-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }

        .about-highlight {
          position: relative;
          min-height: 320px;
          border-radius: 14px;
          overflow: hidden;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: pointer;
        }
        .about-highlight:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.22);
        }

        .about-highlight-overlay {
          position: absolute;
          inset: 0;
          padding: 28px 24px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.55) 0%,
            rgba(0, 0, 0, 0.35) 45%,
            rgba(0, 0, 0, 0.75) 100%
          );
          transition: background 0.3s ease;
        }
        .about-highlight:hover .about-highlight-overlay {
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.6) 0%,
            rgba(0, 0, 0, 0.45) 45%,
            rgba(0, 0, 0, 0.85) 100%
          );
        }

        .about-highlight-num {
          display: block;
          font-size: 38px;
          font-weight: 900;
          color: #ffffff;
          line-height: 1;
          letter-spacing: -0.5px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
        }

        .about-highlight-content {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .about-highlight-title {
          font-size: 18px;
          font-weight: 800;
          color: #ffffff;
          margin: 0;
          line-height: 1.3;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.55);
        }

        .about-highlight-desc {
          font-size: 13.5px;
          line-height: 1.6;
          color: #e5e7eb;
          margin: 0;
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 1100px) {
          .about-highlights-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .about-highlight {
            min-height: 280px;
          }
        }

        @media (max-width: 900px) {
          .about-who-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }
          .about-image-frame img {
            max-height: 420px;
          }
        }

        @media (max-width: 800px) {
          .about-hero {
            padding: 80px 24px 70px;
          }
          .about-hero-title {
            font-size: 42px;
          }
          .about-hero-subtitle {
            font-size: 17px;
          }

          .about-section {
            padding: 70px 24px;
          }
          .about-block-title {
            font-size: 28px;
          }
          .about-block-text {
            font-size: 16px;
          }

          .about-two-col {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .about-card {
            padding: 32px 24px;
          }

          .about-highlights-head {
            margin-bottom: 40px;
          }
        }

        @media (max-width: 500px) {
          .about-hero {
            padding: 60px 20px 50px;
          }
          .about-hero-title {
            font-size: 34px;
          }
          .about-hero-subtitle {
            font-size: 15px;
          }
          .about-section {
            padding: 50px 20px;
          }
          .about-image-frame {
            border-radius: 12px;
          }
          .about-image-frame img {
            max-height: 320px;
          }
          .about-highlights-grid {
            grid-template-columns: 1fr;
            gap: 18px;
          }
          .about-highlight {
            min-height: 240px;
          }
          .about-highlight-overlay {
            padding: 22px 20px;
          }
          .about-highlight-num {
            font-size: 30px;
          }
          .about-highlight-title {
            font-size: 17px;
          }
          .about-highlight-desc {
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
}

export default AboutPage;